"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
	BadgePercent,
	ChevronDown,
	CreditCard,
	Link as LinkIcon,
	Loader2,
	LockKeyhole,
	Mail,
	Rocket,
	ShieldCheck,
	Tag,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	generateEventId,
	getMetaCookies,
	trackInitiateCheckoutPixel,
} from "@/components/analytics/meta_pixel_events";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { GlobalGateway, IndiaGateway } from "@/lib/db";
import { FIRST_TIME_OFFER_EMAIL_KEY } from "@/lib/first-time-offer";
import {
	trackGoogleAdsCheckoutStart,
	trackGoogleAdsEvent,
	trackGoogleAdsPaymentInfo,
} from "@/lib/google-ads-events";
import {
	getAttributionSummary,
	getMetaCustomData,
	trackConversionWithJourney,
} from "@/lib/journey-tracker";

// YouTube URL validation patterns
const youtubeChannelRegex =
	/^https?:\/\/(www\.)?youtube\.com\/(@[\w-]+|channel\/[\w-]+|c\/[\w-]+)/i;
const youtubeVideoRegex =
	/^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+/i;

const checkoutSchema = z.object({
	firstName: z.string().min(1, "Name is required"),
	email: z.string().email("Valid email is required"),
	phone: z.string().optional(),
	contentUrl: z.string().optional(),
});

// Helper function for dynamic label/placeholder based on service type
const getContentFieldConfig = (type: string) => {
	switch (type) {
		case "views":
			return {
				label: "Video URL",
				placeholder: "https://youtube.com/watch?v=...",
				required: true,
			};
		case "subscribers":
		case "monetization":
			return {
				label: "Channel URL",
				placeholder: "https://youtube.com/@...",
				required: true,
			};
		case "editor":
			return {
				label: "Project Details",
				placeholder: "Describe your project requirements...",
				required: false,
			};
		default:
			return {
				label: "Content URL",
				placeholder: "Enter URL or details...",
				required: true,
			};
	}
};

type CheckoutInput = z.infer<typeof checkoutSchema>;
type LegacyGateway = "stripe" | "payu" | null;

interface CouponPreview {
	couponsSupported: boolean;
	isFirstTimeEmail: boolean;
	coupon: {
		id: string;
		code: string;
		name: string;
		description: string | null;
		discountType: string;
		discountValue: number;
		currency: string | null;
		firstTimeOnly: boolean;
		autoApply: boolean;
		stripeCouponId: string | null;
	} | null;
	originalAmount: number;
	discountAmount: number;
	finalAmount: number;
	currency: "usd" | "inr";
	message: string | null;
	reason: string | null;
}

interface CouponUiState {
	draft: string;
	manualCode: string | null;
	preview: CouponPreview | null;
	loading: boolean;
	error: string | null;
	skip: boolean;
	hasUserApplied: boolean;
}

interface PublicCouponOption {
	id: string;
	code: string;
	name: string;
	description: string | null;
	discountType: string;
	discountValue: number;
	currency: string | null;
	firstTimeOnly: boolean;
	autoApply: boolean;
	eligible: boolean;
	reason: string | null;
	discountAmount: number;
	finalAmount: number;
}

interface PublicCouponsResponse {
	couponsSupported: boolean;
	coupons: PublicCouponOption[];
	reason?: string | null;
}

interface CouponBrowserState {
	open: boolean;
	loading: boolean;
	loaded: boolean;
	coupons: PublicCouponOption[];
	error: string | null;
}

const INITIAL_COUPON_STATE: CouponUiState = {
	draft: "",
	manualCode: null,
	preview: null,
	loading: false,
	error: null,
	skip: false,
	hasUserApplied: false,
};

const INITIAL_COUPON_BROWSER_STATE: CouponBrowserState = {
	open: false,
	loading: false,
	loaded: false,
	coupons: [],
	error: null,
};

const CHECKOUT_DRAFT_STORAGE_KEY = "highrays:checkout-drafts:v1";
const DEFAULT_PROMOTED_COUPON_CODE = "FIRST10";

interface StoredCheckoutDraft {
	values: CheckoutInput;
	couponDraft: string;
	manualCouponCode: string | null;
	skipCoupon: boolean;
	updatedAt: string;
}

const AMOUNT_FORMATTERS = {
	inr: {
		whole: new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}),
		decimal: new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}),
	},
	usd: {
		whole: new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}),
		decimal: new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}),
	},
} as const;

interface PricingPlan {
	id: number;
	name: string;
	tier?: string | null;
	inr: number;
	usd: number;
	type: string; // 'views' | 'subscribers' | 'monetization' | 'editor'

	// India payment config
	india_gateway: IndiaGateway;
	india_payu_product_info: string | null;
	india_stripe_price_id: string | null;

	// Global payment config
	global_gateway: GlobalGateway;
	global_stripe_price_id: string | null;

	// Legacy fields (for backward compatibility)
	payment_gateway?: LegacyGateway;
}

interface CheckoutDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	plan: PricingPlan | null;
	currency: "inr" | "usd";
}

function normalizeGatewayText(value: string | null | undefined): string | null {
	const normalized = value?.trim().toLowerCase();
	return normalized || null;
}

function resolveLegacyGlobalGateway(
	gateway?: LegacyGateway | string | null,
): GlobalGateway {
	if (normalizeGatewayText(gateway) === "stripe") return "stripe";
	return null;
}

function resolveCheckoutGateway(
	plan: PricingPlan,
	isIndia: boolean,
): IndiaGateway | GlobalGateway {
	if (isIndia) {
		const indiaGateway = normalizeGatewayText(plan.india_gateway);
		return indiaGateway === "payu" ? indiaGateway : "payu";
	}

	const globalGateway = normalizeGatewayText(plan.global_gateway);
	return (
		(globalGateway === "stripe" ? globalGateway : null) ??
		resolveLegacyGlobalGateway(plan.payment_gateway) ??
		"stripe"
	);
}

function formatCheckoutAmount(value: number, currency: "inr" | "usd"): string {
	const hasMinorUnits = Math.round(value * 100) % 100 !== 0;
	return AMOUNT_FORMATTERS[currency][
		hasMinorUnits ? "decimal" : "whole"
	].format(value);
}

function getDiscountLabel(
	preview: CouponPreview | null,
	currency: "inr" | "usd",
): string {
	if (!preview?.coupon) return "";
	if (preview.coupon.discountType === "percent") {
		return `${preview.coupon.discountValue}% off`;
	}
	return `${formatCheckoutAmount(preview.discountAmount, currency)} off`;
}

function getCouponOptionLabel(
	coupon: PublicCouponOption,
	currency: "inr" | "usd",
): string {
	if (coupon.discountType === "percent") {
		return `${coupon.discountValue}% off`;
	}
	return `${formatCheckoutAmount(coupon.discountAmount, currency)} off`;
}

function normalizeCouponDraft(code: string): string {
	return code.trim().replace(/\s+/g, "").toUpperCase();
}

function getCouponEmailVerificationMessage(code: string): string {
	return `Enter your email above to verify ${code}.`;
}

function submitPayUCheckoutHtml(html: string): void {
	const wrapper = document.createElement("div");
	wrapper.hidden = true;
	wrapper.setAttribute("data-payu-checkout", "true");
	wrapper.innerHTML = html;

	const form = wrapper.querySelector("form");
	if (!(form instanceof HTMLFormElement)) {
		throw new Error("PayU checkout form was not returned");
	}

	document.body.appendChild(wrapper);
	form.submit();
}

function getCheckoutDraftKey(planId: number, currency: "inr" | "usd"): string {
	return `${planId}:${currency}`;
}

function normalizeStoredCheckoutValues(
	values: Partial<CheckoutInput> | null | undefined,
	fallbackEmail = "",
): CheckoutInput {
	return {
		firstName: typeof values?.firstName === "string" ? values.firstName : "",
		email:
			typeof values?.email === "string" && values.email
				? values.email
				: fallbackEmail,
		phone: typeof values?.phone === "string" ? values.phone : "",
		contentUrl: typeof values?.contentUrl === "string" ? values.contentUrl : "",
	};
}

function readCheckoutDraft(
	planId: number,
	currency: "inr" | "usd",
): StoredCheckoutDraft | null {
	try {
		const raw = window.localStorage.getItem(CHECKOUT_DRAFT_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Record<
			string,
			Partial<StoredCheckoutDraft> | undefined
		>;
		const draft = parsed[getCheckoutDraftKey(planId, currency)];
		if (!draft || typeof draft !== "object") return null;

		return {
			values: normalizeStoredCheckoutValues(draft.values),
			couponDraft:
				typeof draft.couponDraft === "string" ? draft.couponDraft : "",
			manualCouponCode:
				typeof draft.manualCouponCode === "string"
					? draft.manualCouponCode
					: null,
			skipCoupon: Boolean(draft.skipCoupon),
			updatedAt:
				typeof draft.updatedAt === "string"
					? draft.updatedAt
					: new Date().toISOString(),
		};
	} catch (_error) {
		return null;
	}
}

function writeCheckoutDraft(
	planId: number,
	currency: "inr" | "usd",
	draft: StoredCheckoutDraft,
): void {
	try {
		const raw = window.localStorage.getItem(CHECKOUT_DRAFT_STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : {};
		const drafts =
			parsed && typeof parsed === "object"
				? (parsed as Record<string, StoredCheckoutDraft>)
				: {};
		drafts[getCheckoutDraftKey(planId, currency)] = draft;
		window.localStorage.setItem(
			CHECKOUT_DRAFT_STORAGE_KEY,
			JSON.stringify(drafts),
		);
	} catch (_error) {
		// Draft persistence should never block checkout.
	}
}

function canSelectPublicCoupon(coupon: PublicCouponOption): boolean {
	return (
		coupon.eligible ||
		coupon.reason === "Enter an email to check first-time eligibility"
	);
}

function getPlanAccent(plan: Pick<PricingPlan, "name" | "tier">) {
	const label = (plan.tier || plan.name).toLowerCase();
	if (label.includes("bronze")) {
		return {
			background: "#a76a24",
			foreground: "#fffaf0",
			price: "#a45512",
			border: "#f2d6ac",
			surface: "#fff8ed",
			saving: "#8a4b12",
		};
	}

	return {
		background: "#dc2626",
		foreground: "#ffffff",
		price: "#dc2626",
		border: "#fee2e2",
		surface: "#fef2f2",
		saving: "#991b1b",
	};
}

type CheckoutGateway = Exclude<IndiaGateway | GlobalGateway, null>;

type CouponStateSetter = (
	value: CouponUiState | ((current: CouponUiState) => CouponUiState),
) => void;

type CouponBrowserStateSetter = (
	value:
		| CouponBrowserState
		| ((current: CouponBrowserState) => CouponBrowserState),
) => void;

function syncCheckoutDialogEvent(open: boolean) {
	window.dispatchEvent(
		new CustomEvent("highrays:checkout-dialog", { detail: { open } }),
	);
	return () => {
		window.dispatchEvent(
			new CustomEvent("highrays:checkout-dialog", {
				detail: { open: false },
			}),
		);
	};
}

interface ResetCheckoutDraftOptions {
	open: boolean;
	planId: number | undefined;
	currency: "inr" | "usd";
	form: UseFormReturn<CheckoutInput>;
	setCouponState: CouponStateSetter;
	setCouponBrowser: CouponBrowserStateSetter;
	activeDraftKeyRef: { current: string | null };
}

function resetCheckoutDraft({
	open,
	planId,
	currency,
	form,
	setCouponState,
	setCouponBrowser,
	activeDraftKeyRef,
}: ResetCheckoutDraftOptions): void {
	if (!open || !planId) return;
	const draftKey = getCheckoutDraftKey(planId, currency);
	const storedDraft = readCheckoutDraft(planId, currency);
	const storedOfferEmail =
		window.localStorage.getItem(FIRST_TIME_OFFER_EMAIL_KEY) ?? "";
	form.reset(
		storedDraft
			? storedDraft.values
			: normalizeStoredCheckoutValues(null, storedOfferEmail),
	);
	setCouponState({
		...INITIAL_COUPON_STATE,
		draft: storedDraft?.couponDraft ?? "",
		manualCode: storedDraft?.manualCouponCode ?? null,
		skip: storedDraft?.skipCoupon ?? false,
	});
	setCouponBrowser(INITIAL_COUPON_BROWSER_STATE);
	activeDraftKeyRef.current = draftKey;
}

interface PersistCheckoutDraftOptions {
	open: boolean;
	planId: number | undefined;
	currency: "inr" | "usd";
	currentDraftKey: string | null;
	activeDraftKeyRef: { current: string | null };
	watchedFirstName: string | undefined;
	watchedEmail: string | undefined;
	watchedPhone: string | undefined;
	watchedContentUrl: string | undefined;
	couponDraft: string;
	manualCouponCode: string | null;
	skipCoupon: boolean;
}

function persistCheckoutDraft({
	open,
	planId,
	currency,
	currentDraftKey,
	activeDraftKeyRef,
	watchedFirstName,
	watchedEmail,
	watchedPhone,
	watchedContentUrl,
	couponDraft,
	manualCouponCode,
	skipCoupon,
}: PersistCheckoutDraftOptions): void {
	if (!open || !planId || !currentDraftKey) return;
	if (activeDraftKeyRef.current !== currentDraftKey) return;

	writeCheckoutDraft(planId, currency, {
		values: normalizeStoredCheckoutValues({
			firstName: watchedFirstName,
			email: watchedEmail,
			phone: watchedPhone,
			contentUrl: watchedContentUrl,
		}),
		couponDraft,
		manualCouponCode,
		skipCoupon,
		updatedAt: new Date().toISOString(),
	});
}

interface PreviewCheckoutCouponOptions {
	open: boolean;
	plan: PricingPlan | null;
	watchedEmail: string;
	isIndia: boolean;
	isCouponGateway: boolean;
	manualCouponCode: string | null;
	skipCoupon: boolean;
	setCouponState: CouponStateSetter;
}

function previewCheckoutCoupon({
	open,
	plan,
	watchedEmail,
	isIndia,
	isCouponGateway,
	manualCouponCode,
	skipCoupon,
	setCouponState,
}: PreviewCheckoutCouponOptions) {
	const emailResult = checkoutSchema.shape.email.safeParse(watchedEmail);

	if (!open || !plan || !isCouponGateway || skipCoupon) {
		setCouponState((current) => ({
			...current,
			loading: false,
			error: null,
			preview: null,
		}));
		return;
	}

	if (!emailResult.success) {
		setCouponState((current) => ({
			...current,
			loading: false,
			preview: null,
			error:
				manualCouponCode && current.hasUserApplied
					? getCouponEmailVerificationMessage(manualCouponCode)
					: null,
		}));
		return;
	}

	let isCancelled = false;
	setCouponState((current) => ({
		...current,
		loading: true,
		error: null,
	}));

	const timer = window.setTimeout(
		async () => {
			try {
				const response = await fetch("/api/coupons/preview", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						pricingId: plan.id,
						email: watchedEmail,
						region: isIndia ? "india" : "global",
						couponCode: manualCouponCode,
						skipAutoApply: false,
					}),
				});

				if (!response.ok) {
					const preview = (await response.json()) as CouponPreview;
					if (isCancelled) return;
					setCouponState((current) => ({
						...current,
						loading: false,
						preview: null,
						error: preview.reason || "Coupon could not be checked",
					}));
					return;
				}

				const preview = (await response.json()) as CouponPreview;
				if (isCancelled) return;

				setCouponState((current) => ({
					...current,
					loading: false,
					preview,
					error:
						manualCouponCode && !preview.coupon && current.hasUserApplied
							? preview.reason || "Coupon is not available"
							: null,
				}));
			} catch (_error) {
				if (!isCancelled) {
					setCouponState((current) => ({
						...current,
						loading: false,
						preview: null,
						error: "Coupon check is unavailable right now",
					}));
				}
			}
		},
		manualCouponCode ? 0 : 450,
	);

	return () => {
		isCancelled = true;
		window.clearTimeout(timer);
	};
}

interface BrowseCouponsOptions {
	plan: PricingPlan | null;
	isCouponGateway: boolean;
	couponBrowser: CouponBrowserState;
	isIndia: boolean;
	watchedEmail: string;
	setCouponBrowser: CouponBrowserStateSetter;
}

async function browseCoupons({
	plan,
	isCouponGateway,
	couponBrowser,
	isIndia,
	watchedEmail,
	setCouponBrowser,
}: BrowseCouponsOptions): Promise<void> {
	if (!plan || !isCouponGateway) return;

	const willOpen = !couponBrowser.open;
	setCouponBrowser((current) => ({
		...current,
		open: willOpen,
		error: willOpen ? current.error : null,
	}));

	if (!willOpen || couponBrowser.loading) return;

	setCouponBrowser((current) => ({
		...current,
		open: true,
		loading: true,
		error: null,
	}));

	try {
		const params = new URLSearchParams({
			pricingId: String(plan.id),
			region: isIndia ? "india" : "global",
		});
		const emailResult = checkoutSchema.shape.email.safeParse(watchedEmail);
		if (emailResult.success) {
			params.set("email", watchedEmail.trim());
		}

		const response = await fetch(`/api/coupons/public?${params.toString()}`);

		if (!response.ok) {
			const payload = (await response.json()) as PublicCouponsResponse;
			setCouponBrowser((current) => ({
				...current,
				loading: false,
				loaded: true,
				coupons: [],
				error:
					payload.reason ||
					"Public coupons are not available for this checkout",
			}));
			return;
		}

		const payload = (await response.json()) as PublicCouponsResponse;

		if (!payload.couponsSupported) {
			setCouponBrowser((current) => ({
				...current,
				loading: false,
				loaded: true,
				coupons: [],
				error:
					payload.reason ||
					"Public coupons are not available for this checkout",
			}));
			return;
		}

		setCouponBrowser((current) => ({
			...current,
			loading: false,
			loaded: true,
			coupons: payload.coupons ?? [],
			error: null,
		}));
	} catch (_error) {
		setCouponBrowser((current) => ({
			...current,
			loading: false,
			loaded: true,
			coupons: [],
			error: "Coupon list is unavailable right now",
		}));
	}
}

function applyCoupon({
	couponDraft,
	watchedEmail,
	setCouponState,
}: {
	couponDraft: string;
	watchedEmail: string;
	setCouponState: CouponStateSetter;
}): void {
	const normalizedCode = normalizeCouponDraft(couponDraft);
	if (!normalizedCode) {
		setCouponState((current) => ({
			...current,
			error: "Enter a coupon code",
		}));
		return;
	}
	const emailResult = checkoutSchema.shape.email.safeParse(watchedEmail);
	setCouponState((current) => ({
		...current,
		draft: normalizedCode,
		manualCode: normalizedCode,
		preview: emailResult.success ? current.preview : null,
		loading: emailResult.success ? current.loading : false,
		error: emailResult.success
			? null
			: getCouponEmailVerificationMessage(normalizedCode),
		skip: false,
		hasUserApplied: true,
	}));
}

function changeCouponDraft({
	nextDraft,
	setCouponState,
}: {
	nextDraft: string;
	setCouponState: CouponStateSetter;
}): void {
	const nextCode = normalizeCouponDraft(nextDraft);
	setCouponState((current) => ({
		...current,
		draft: nextDraft,
		manualCode:
			current.manualCode && nextCode === current.manualCode
				? current.manualCode
				: null,
		preview:
			current.manualCode && nextCode === current.manualCode
				? current.preview
				: null,
		error: null,
		skip: current.skip ? false : current.skip,
		hasUserApplied:
			current.manualCode && nextCode === current.manualCode
				? current.hasUserApplied
				: false,
	}));
}

function applyCouponOption({
	code,
	watchedEmail,
	setCouponState,
	setCouponBrowser,
}: {
	code: string;
	watchedEmail: string;
	setCouponState: CouponStateSetter;
	setCouponBrowser: CouponBrowserStateSetter;
}): void {
	const normalizedCode = normalizeCouponDraft(code);
	const emailResult = checkoutSchema.shape.email.safeParse(watchedEmail);
	setCouponState((current) => ({
		...current,
		draft: normalizedCode,
		manualCode: normalizedCode,
		preview: emailResult.success ? current.preview : null,
		loading: emailResult.success ? current.loading : false,
		error: emailResult.success
			? null
			: getCouponEmailVerificationMessage(normalizedCode),
		skip: false,
		hasUserApplied: true,
	}));
	setCouponBrowser((current) => ({
		...current,
		open: false,
	}));
}

function skipCheckoutCoupon(setCouponState: CouponStateSetter): void {
	setCouponState((current) => ({
		...current,
		draft: "",
		manualCode: null,
		preview: null,
		error: null,
		skip: true,
		hasUserApplied: false,
	}));
}

interface SubmitCheckoutOptions {
	data: CheckoutInput;
	contentFieldConfig: ReturnType<typeof getContentFieldConfig>;
	form: UseFormReturn<CheckoutInput>;
	plan: PricingPlan;
	gateway: CheckoutGateway;
	isIndia: boolean;
	price: number;
	finalPrice: number;
	skipCoupon: boolean;
	activeCoupon: CouponPreview["coupon"] | undefined;
	manualCouponCode: string | null;
	setIsLoading: (value: boolean) => void;
}

async function submitCheckout({
	data,
	contentFieldConfig,
	form,
	plan,
	gateway,
	isIndia,
	price,
	finalPrice,
	skipCoupon,
	activeCoupon,
	manualCouponCode,
	setIsLoading,
}: SubmitCheckoutOptions): Promise<void> {
	// Validate contentUrl if required for this plan type
	if (contentFieldConfig.required && !data.contentUrl?.trim()) {
		form.setError("contentUrl", {
			type: "manual",
			message: `${contentFieldConfig.label} is required`,
		});
		return;
	}

	// Validate YouTube URL format based on plan type
	if (data.contentUrl?.trim()) {
		const url = data.contentUrl.trim();
		if (plan.type === "views") {
			// Views require video URL
			if (!youtubeVideoRegex.test(url)) {
				form.setError("contentUrl", {
					type: "manual",
					message:
						"Please enter a valid YouTube video URL (e.g., youtube.com/watch?v=...)",
				});
				return;
			}
		} else if (plan.type === "subscribers" || plan.type === "monetization") {
			// Subscribers and monetization require channel URL
			if (!youtubeChannelRegex.test(url)) {
				form.setError("contentUrl", {
					type: "manual",
					message:
						"Please enter a valid YouTube channel URL (e.g., youtube.com/@channelname)",
				});
				return;
			}
		}
	}

	if (gateway === "payu" && !data.phone?.trim()) {
		form.setError("phone", {
			type: "manual",
			message: "Phone is required for PayU checkout",
		});
		return;
	}

	setIsLoading(true);

	// Track InitiateCheckout client-side for deduplication
	const metaEventId = generateEventId();
	const { fbc, fbp } = getMetaCookies();

	// Get journey/attribution data
	const attribution = getAttributionSummary();
	const metaCustomData = getMetaCustomData();
	const couponCode = skipCoupon
		? null
		: (activeCoupon?.code ?? manualCouponCode ?? null);

	trackInitiateCheckoutPixel({
		value: price,
		currency: isIndia ? "INR" : "USD",
		contentName: plan.name,
		contentIds: [String(plan.id)],
		eventId: metaEventId,
	});

	// Track in Umami with journey context
	trackConversionWithJourney("checkout_start", {
		plan: plan.name,
		price,
		currency: isIndia ? "INR" : "USD",
	});
	trackGoogleAdsPaymentInfo({
		planId: plan.id,
		planName: plan.name,
		value: finalPrice,
		currency: isIndia ? "INR" : "USD",
		gateway,
		coupon: couponCode,
	});

	try {
		if (gateway === "stripe") {
			// Stripe checkout - pass region info
			const response = await fetch("/api/checkout/stripe", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					pricingId: plan.id,
					firstName: data.firstName,
					email: data.email,
					phone: data.phone,
					region: isIndia ? "india" : "global",
					contentUrl: data.contentUrl,
					couponCode,
					metaEventId, // For deduplication
					fbc,
					fbp,
					// Journey/attribution data
					attribution,
					metaCustomData,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to create checkout session");
			}

			const { url } = await response.json();
			if (url) {
				window.location.href = url;
			} else {
				throw new Error("No checkout URL received");
			}
		} else if (gateway === "payu") {
			// PayU checkout - always for India
			const response = await fetch("/api/checkout/payu", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					pricingId: plan.id,
					firstName: data.firstName,
					email: data.email,
					phone: data.phone,
					region: "india",
					contentUrl: data.contentUrl,
					couponCode,
					metaEventId, // For deduplication
					fbc,
					fbp,
					// Journey/attribution data
					attribution,
					metaCustomData,
				}),
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to create payment");
			}

			const payuData = (await response.json()) as {
				html?: string;
				txnid?: string;
			};
			if (!payuData.html) {
				throw new Error("PayU checkout form was not returned");
			}

			submitPayUCheckoutHtml(payuData.html);
		}
	} catch (error) {
		console.error("Checkout error:", error);
		trackGoogleAdsEvent("checkout_error", {
			event_category: "checkout",
			event_label: plan.name,
			payment_type: gateway,
		});
		toast.error(error instanceof Error ? error.message : "Checkout failed");
	} finally {
		setIsLoading(false);
	}
}

interface CheckoutFormContentProps {
	form: UseFormReturn<CheckoutInput>;
	plan: PricingPlan;
	gateway: CheckoutGateway;
	contentFieldConfig: ReturnType<typeof getContentFieldConfig>;
	couponState: CouponUiState;
	couponBrowser: CouponBrowserState;
	activeCoupon: CouponPreview["coupon"] | undefined;
	discountLabel: string;
	currency: "inr" | "usd";
	planAccent: ReturnType<typeof getPlanAccent> | null;
	isLoading: boolean;
	isCouponGateway: boolean;
	finalPrice: number;
	onCouponDraftChange: (value: string) => void;
	onApplyCoupon: () => void;
	onBrowseCoupons: () => void;
	onApplyCouponOption: (code: string) => void;
	onSkipCoupon: () => void;
	onSubmit: (data: CheckoutInput) => Promise<void>;
	onFormFocus: () => void;
}

interface CheckoutCouponPanelProps {
	couponState: CouponUiState;
	couponBrowser: CouponBrowserState;
	activeCoupon: CouponPreview["coupon"] | undefined;
	discountLabel: string;
	currency: "inr" | "usd";
	planAccent: ReturnType<typeof getPlanAccent> | null;
	onCouponDraftChange: (value: string) => void;
	onApplyCoupon: () => void;
	onBrowseCoupons: () => void;
	onApplyCouponOption: (code: string) => void;
	onSkipCoupon: () => void;
}

function CheckoutCouponPanel({
	couponState,
	couponBrowser,
	activeCoupon,
	discountLabel,
	currency,
	planAccent,
	onCouponDraftChange,
	onApplyCoupon,
	onBrowseCoupons,
	onApplyCouponOption,
	onSkipCoupon,
}: CheckoutCouponPanelProps) {
	const couponDraft = couponState.draft;
	const couponLoading = couponState.loading;
	const couponError = couponState.error;
	const normalizedCouponDraft = normalizeCouponDraft(couponDraft);
	const isApplyCheckingCurrentCode =
		couponLoading &&
		normalizedCouponDraft.length > 0 &&
		normalizedCouponDraft === couponState.manualCode;

	return (
		<div className="rounded-lg border border-zinc-200 bg-white p-3">
			<div className="mb-2 flex items-center justify-between gap-3">
				<div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
					<Tag className="size-4 text-red-600" />
					<span>Coupon code</span>
				</div>
				{couponLoading && (
					<div className="flex items-center gap-1 text-xs text-zinc-500">
						<Loader2 className="size-3 animate-spin" />
						Checking
					</div>
				)}
			</div>
			<div
				className="mb-2 flex flex-wrap items-center gap-2 rounded-[0.625rem] border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700"
				data-testid="checkout-first-order-offer"
			>
				<span>First-order offer</span>
				<button
					type="button"
					className="rounded border border-red-200 bg-white px-2 py-1 font-mono text-[11px] font-black text-red-700 transition-colors hover:border-red-300 hover:bg-red-100"
					onClick={() => onApplyCouponOption(DEFAULT_PROMOTED_COUPON_CODE)}
				>
					{DEFAULT_PROMOTED_COUPON_CODE}
				</button>
				<span className="text-red-600/80">
					Use {DEFAULT_PROMOTED_COUPON_CODE} for 10% off.
				</span>
			</div>
			<div className="flex gap-2">
				<Input
					value={couponDraft}
					onChange={(event) => onCouponDraftChange(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							onApplyCoupon();
						}
					}}
					placeholder="FIRST10"
					className="uppercase"
				/>
				<Button
					type="button"
					variant="outline"
					onClick={onApplyCoupon}
					disabled={isApplyCheckingCurrentCode}
				>
					Apply code
				</Button>
			</div>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className="mt-2 h-9 w-full justify-center gap-2 rounded-[0.625rem] border-red-100 bg-white px-3 text-sm font-semibold text-red-700 shadow-none hover:border-red-200 hover:bg-red-50/60 hover:text-red-800 focus-visible:ring-red-500/15"
				onClick={onBrowseCoupons}
				aria-expanded={couponBrowser.open}
				disabled={couponBrowser.loading}
				data-testid="checkout-browse-coupons-button"
			>
				<Tag
					className="size-3.5 text-red-500"
					data-testid="checkout-browse-coupons-icon"
				/>
				<span>Browse coupons</span>
				{couponBrowser.loading ? (
					<Loader2 className="size-3 animate-spin" />
				) : (
					<ChevronDown
						className={`size-3.5 text-red-500 transition-transform ${
							couponBrowser.open ? "rotate-180" : ""
						}`}
					/>
				)}
			</Button>
			{couponBrowser.open && (
				<div className="mt-3 space-y-2 rounded-md border border-zinc-100 bg-zinc-50 p-2">
					{couponBrowser.loading && (
						<div className="flex items-center gap-2 px-2 py-3 text-sm text-zinc-500">
							<Loader2 className="size-4 animate-spin" />
							Searching public coupons
						</div>
					)}
					{couponBrowser.error && !couponBrowser.loading && (
						<p className="px-2 py-3 text-sm font-medium text-red-600">
							{couponBrowser.error}
						</p>
					)}
					{couponBrowser.loaded &&
						!couponBrowser.loading &&
						!couponBrowser.error &&
						couponBrowser.coupons.length === 0 && (
							<p className="px-2 py-3 text-sm text-zinc-500">
								No public coupons are available for this plan.
							</p>
						)}
					{couponBrowser.coupons.map((coupon) => (
						<div
							key={coupon.id}
							className="rounded-md border border-zinc-200 bg-white p-3"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="text-sm font-bold text-zinc-950">
										{coupon.name}
									</p>
									<div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
										<span className="rounded bg-zinc-100 px-2 py-1 font-mono font-bold text-zinc-800">
											{coupon.code}
										</span>
										<span className="font-semibold text-red-600">
											{getCouponOptionLabel(coupon, currency)}
										</span>
										{coupon.firstTimeOnly && (
											<span className="font-medium text-zinc-500">
												First-time only
											</span>
										)}
									</div>
									<p className="mt-2 text-xs leading-relaxed text-zinc-500">
										{coupon.eligible
											? (coupon.description ??
												`Pay ${formatCheckoutAmount(
													coupon.finalAmount,
													currency,
												)} with this coupon.`)
											: coupon.reason}
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									aria-label={`Apply ${coupon.code}`}
									disabled={!canSelectPublicCoupon(coupon)}
									onClick={() => onApplyCouponOption(coupon.code)}
								>
									{coupon.eligible ? "Apply" : "Use"}
								</Button>
							</div>
						</div>
					))}
				</div>
			)}
			{activeCoupon && (
				<div
					className="mt-3 rounded-[0.625rem] border px-3 py-2 text-sm"
					data-testid="checkout-active-coupon"
					style={{
						backgroundColor: planAccent?.surface,
						borderColor: planAccent?.border,
						color: planAccent?.saving,
					}}
				>
					<div className="flex items-center gap-2 font-semibold">
						<BadgePercent
							className="size-4"
							style={{ color: planAccent?.price }}
						/>
						<span>
							{activeCoupon.code} applied: {discountLabel}
						</span>
					</div>
					<p className="mt-1 text-xs">
						Email-based first-time eligibility is verified again on the server
						before payment.
					</p>
				</div>
			)}
			{couponError && (
				<p className="mt-2 text-xs font-medium text-red-600">{couponError}</p>
			)}
			{activeCoupon && (
				<button
					type="button"
					className="mt-2 text-xs font-medium text-zinc-500 underline underline-offset-4 transition-colors hover:text-zinc-900"
					onClick={onSkipCoupon}
				>
					Continue without discount
				</button>
			)}
		</div>
	);
}

function CheckoutFormContent({
	form,
	plan,
	gateway,
	contentFieldConfig,
	couponState,
	couponBrowser,
	activeCoupon,
	discountLabel,
	currency,
	planAccent,
	isLoading,
	isCouponGateway,
	finalPrice,
	onCouponDraftChange,
	onApplyCoupon,
	onBrowseCoupons,
	onApplyCouponOption,
	onSkipCoupon,
	onSubmit,
	onFormFocus,
}: CheckoutFormContentProps) {
	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				onFocus={onFormFocus}
				className="space-y-4"
			>
				<FormField
					control={form.control}
					name="firstName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input placeholder="Your name" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input type="email" placeholder="your@email.com" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="phone"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								Phone
								{gateway === "payu" ? (
									<span className="text-destructive ml-1">*</span>
								) : (
									<span className="text-zinc-500 ml-1">(Optional)</span>
								)}
							</FormLabel>
							<FormControl>
								<Input
									type="tel"
									placeholder={
										currency === "inr" ? "+91 XXXXXXXXXX" : "+1 XXX-XXX-XXXX"
									}
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="contentUrl"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								{contentFieldConfig.label}
								{contentFieldConfig.required && (
									<span className="text-destructive ml-1">*</span>
								)}
							</FormLabel>
							<FormControl>
								{plan.type === "editor" ? (
									<Textarea
										placeholder={contentFieldConfig.placeholder}
										rows={4}
										{...field}
									/>
								) : (
									<Input
										placeholder={contentFieldConfig.placeholder}
										{...field}
									/>
								)}
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{isCouponGateway && (
					<CheckoutCouponPanel
						couponState={couponState}
						couponBrowser={couponBrowser}
						activeCoupon={activeCoupon}
						discountLabel={discountLabel}
						currency={currency}
						planAccent={planAccent}
						onCouponDraftChange={onCouponDraftChange}
						onApplyCoupon={onApplyCoupon}
						onBrowseCoupons={onBrowseCoupons}
						onApplyCouponOption={onApplyCouponOption}
						onSkipCoupon={onSkipCoupon}
					/>
				)}
				<div
					className="bottom-0 z-10 -mx-5 space-y-3 bg-white/95 px-5 pb-2 pt-2 backdrop-blur sm:sticky sm:-mx-8 sm:px-8"
					data-testid="checkout-action-stack"
				>
					<Button type="submit" className="w-full" disabled={isLoading}>
						{isLoading ? (
							<Loader2 className="mr-2 size-4 animate-spin" />
						) : activeCoupon ? (
							<LockKeyhole className="mr-2 size-4" />
						) : (
							<CreditCard className="mr-2 size-4" />
						)}
						{activeCoupon ? `Claim ${discountLabel} and pay` : "Pay"}{" "}
						{formatCheckoutAmount(finalPrice, currency)}
					</Button>

					<div
						className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-[0.625rem] bg-zinc-100 p-3 text-xs font-medium text-zinc-600"
						data-testid="checkout-security-strip"
					>
						<span className="flex items-center gap-1.5">
							<ShieldCheck className="size-3.5" />
							Secure checkout
						</span>
						<span className="flex items-center gap-1.5">
							<CreditCard className="size-3.5" />
							{gateway === "stripe" ? "Stripe" : "PayU"}
						</span>
						<span className="flex items-center gap-1.5">
							<Mail className="size-3.5" />
							First-time email verified
						</span>
						<span className="flex items-center gap-1.5">
							<LinkIcon className="size-3.5" />
							Campaign details protected
						</span>
					</div>
				</div>
			</form>
		</Form>
	);
}

interface CheckoutPlanSummaryProps {
	plan: PricingPlan;
	planDisplayName: string;
	planAccent: ReturnType<typeof getPlanAccent> | null;
	activeCoupon: CouponPreview["coupon"] | undefined;
	price: number;
	finalPrice: number;
	couponPreview: CouponPreview | null;
	currency: "inr" | "usd";
}

function CheckoutPlanSummary({
	plan,
	planDisplayName,
	planAccent,
	activeCoupon,
	price,
	finalPrice,
	couponPreview,
	currency,
}: CheckoutPlanSummaryProps) {
	return (
		<div className="mb-5 rounded-lg border border-zinc-200 bg-zinc-50 p-3 shadow-sm">
			<div className="flex items-center gap-3">
				<div
					className="flex size-16 shrink-0 items-center justify-center rounded-lg shadow-lg"
					data-testid="checkout-plan-icon"
					style={{
						backgroundColor: planAccent?.background,
						color: planAccent?.foreground,
					}}
				>
					<Rocket className="size-8" />
				</div>
				<div className="min-w-0 flex-1">
					<p className="truncate text-base font-bold text-zinc-950">
						{planDisplayName}
					</p>
					<p className="mt-1 line-clamp-2 text-sm text-zinc-600">
						{plan.type} campaign checkout
					</p>
				</div>
				<div className="text-right">
					{activeCoupon && (
						<p className="text-sm font-semibold text-zinc-400 line-through">
							{formatCheckoutAmount(price, currency)}
						</p>
					)}
					<p
						className="text-2xl font-extrabold"
						style={{ color: planAccent?.price }}
					>
						{formatCheckoutAmount(finalPrice, currency)}
					</p>
					{activeCoupon && couponPreview && (
						<p
							className="mt-1 rounded px-2 py-0.5 text-xs font-semibold"
							style={{
								backgroundColor: planAccent?.surface,
								color: planAccent?.saving,
							}}
						>
							Save{" "}
							{formatCheckoutAmount(couponPreview.discountAmount, currency)}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}

export function CheckoutDialog({
	open,
	onOpenChange,
	plan,
	currency,
}: CheckoutDialogProps) {
	const [isLoading, setIsLoading] = useState(false);
	const [couponState, setCouponState] =
		useState<CouponUiState>(INITIAL_COUPON_STATE);
	const [couponBrowser, setCouponBrowser] = useState<CouponBrowserState>(
		INITIAL_COUPON_BROWSER_STATE,
	);
	const activeDraftKeyRef = useRef<string | null>(null);
	const trackedCheckoutKeyRef = useRef<string | null>(null);
	const hasTrackedFormStartRef = useRef(false);

	const form = useForm<CheckoutInput>({
		resolver: zodResolver(checkoutSchema),
		defaultValues: {
			firstName: "",
			email: "",
			phone: "",
			contentUrl: "",
		},
	});

	const watchedFirstName = form.watch("firstName");
	const watchedEmail = form.watch("email");
	const watchedPhone = form.watch("phone");
	const watchedContentUrl = form.watch("contentUrl");
	const isIndia = currency === "inr";
	const gateway = plan ? resolveCheckoutGateway(plan, isIndia) : null;
	const price = plan ? (isIndia ? plan.inr : plan.usd) : 0;
	const isCouponGateway = gateway === "stripe" || gateway === "payu";
	const {
		draft: couponDraft,
		manualCode: manualCouponCode,
		preview: couponPreview,
		skip: skipCoupon,
	} = couponState;
	const activeCoupon = skipCoupon ? null : couponPreview?.coupon;
	const finalPrice = activeCoupon ? couponPreview?.finalAmount || price : price;
	const discountLabel = getDiscountLabel(couponPreview, currency);
	const planAccent = plan ? getPlanAccent(plan) : null;
	const planDisplayName = plan ? plan.tier || plan.name : "";
	const currentDraftKey = plan ? getCheckoutDraftKey(plan.id, currency) : null;

	useEffect(() => syncCheckoutDialogEvent(open), [open]);

	useEffect(() => {
		if (!open) {
			trackedCheckoutKeyRef.current = null;
			hasTrackedFormStartRef.current = false;
			return;
		}
		if (!plan || !gateway) return;

		const checkoutKey = `${plan.id}:${currency}`;
		if (trackedCheckoutKeyRef.current === checkoutKey) return;
		trackedCheckoutKeyRef.current = checkoutKey;
		trackGoogleAdsCheckoutStart({
			planId: plan.id,
			planName: plan.name,
			value: price,
			currency: isIndia ? "INR" : "USD",
		});
	}, [currency, gateway, isIndia, open, plan, price]);

	useEffect(() => {
		resetCheckoutDraft({
			open,
			planId: plan?.id,
			currency,
			form,
			setCouponState,
			setCouponBrowser,
			activeDraftKeyRef,
		});
	}, [currency, form, open, plan?.id]);

	useEffect(() => {
		persistCheckoutDraft({
			open,
			planId: plan?.id,
			currency,
			currentDraftKey,
			activeDraftKeyRef,
			watchedFirstName,
			watchedEmail,
			watchedPhone,
			watchedContentUrl,
			couponDraft,
			manualCouponCode,
			skipCoupon,
		});
	}, [
		couponDraft,
		currency,
		currentDraftKey,
		manualCouponCode,
		open,
		plan?.id,
		skipCoupon,
		watchedContentUrl,
		watchedEmail,
		watchedFirstName,
		watchedPhone,
	]);

	useEffect(
		() =>
			previewCheckoutCoupon({
				open,
				plan,
				watchedEmail,
				isIndia,
				isCouponGateway,
				manualCouponCode,
				skipCoupon,
				setCouponState,
			}),
		[
			open,
			plan,
			watchedEmail,
			isIndia,
			isCouponGateway,
			manualCouponCode,
			skipCoupon,
		],
	);

	if (!plan) return null;

	// Get content field config based on plan type
	const contentFieldConfig = getContentFieldConfig(plan.type);

	// If no gateway configured for this region, show error
	if (!gateway) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-[400px]">
					<DialogHeader>
						<DialogTitle>Payment Not Available</DialogTitle>
						<DialogDescription>
							Payment is not configured for your region. Please contact support.
						</DialogDescription>
					</DialogHeader>
					<Button onClick={() => onOpenChange(false)} className="w-full">
						Close
					</Button>
				</DialogContent>
			</Dialog>
		);
	}

	const handleBrowseCoupons = () =>
		browseCoupons({
			plan,
			isCouponGateway,
			couponBrowser,
			isIndia,
			watchedEmail,
			setCouponBrowser,
		});

	const handleApplyCoupon = () =>
		applyCoupon({ couponDraft, watchedEmail, setCouponState });

	const handleCouponDraftChange = (nextDraft: string) =>
		changeCouponDraft({ nextDraft, setCouponState });

	const handleApplyCouponOption = (code: string) =>
		applyCouponOption({
			code,
			watchedEmail,
			setCouponState,
			setCouponBrowser,
		});

	const handleSkipCoupon = () => skipCheckoutCoupon(setCouponState);

	const handleSubmit = (data: CheckoutInput) =>
		submitCheckout({
			data,
			contentFieldConfig,
			form,
			plan,
			gateway,
			isIndia,
			price,
			finalPrice,
			skipCoupon,
			activeCoupon,
			manualCouponCode,
			setIsLoading,
		});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="!z-[100] sm:max-w-[560px] max-h-[92vh] overflow-hidden p-0 flex flex-col bg-white text-zinc-950">
				<DialogHeader className="sr-only">
					<DialogTitle>Complete your purchase</DialogTitle>
					<DialogDescription>
						Secure checkout for {planDisplayName}
					</DialogDescription>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto min-h-0 px-5 pb-5 pt-5 sm:px-8 sm:pb-8 sm:pt-8">
					<CheckoutPlanSummary
						plan={plan}
						planDisplayName={planDisplayName}
						planAccent={planAccent}
						activeCoupon={activeCoupon}
						price={price}
						finalPrice={finalPrice}
						couponPreview={couponPreview}
						currency={currency}
					/>

					<CheckoutFormContent
						form={form}
						plan={plan}
						gateway={gateway}
						contentFieldConfig={contentFieldConfig}
						couponState={couponState}
						couponBrowser={couponBrowser}
						activeCoupon={activeCoupon}
						discountLabel={discountLabel}
						currency={currency}
						planAccent={planAccent}
						isLoading={isLoading}
						isCouponGateway={isCouponGateway}
						finalPrice={finalPrice}
						onCouponDraftChange={handleCouponDraftChange}
						onApplyCoupon={handleApplyCoupon}
						onBrowseCoupons={handleBrowseCoupons}
						onApplyCouponOption={handleApplyCouponOption}
						onSkipCoupon={handleSkipCoupon}
						onSubmit={handleSubmit}
						onFormFocus={() => {
							if (hasTrackedFormStartRef.current) return;
							hasTrackedFormStartRef.current = true;
							trackGoogleAdsEvent("form_start", {
								event_category: "checkout",
								event_label: plan.name,
							});
						}}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
}
